// Considered: ItemCardMolecule / TemplateCardMolecule — both rejected for the card itself.
// ItemCardMolecule renders a `ChannelItem` (ratings, deploy button, syndication, sort_order,
// published_by) — a gallery row here is an org-private composition/Space the caller can `view`,
// never a channel listing, so most required `ChannelItem` fields would be fabricated placeholders.
// TemplateCardMolecule is bound to `TemplateApproach` (schema + approach creation, count chips) —
// a different domain entirely. The card below is built from canonical atoms directly, the same
// pattern `ComposerShellOrganism` uses for its lint-strip/wires-panel (see spec Reuse Audit note).
import React from 'react';
import { useTranslation } from 'localization';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { SpaceDocument } from 'types/space';
import type { CompositionKind } from 'types/composition';
import type { ComponentRole } from '@center-platform/component-catalog';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import GalleryCardPreviewMolecule from '../../molecules/composer/GalleryCardPreviewMolecule';
import { groupGalleryEntriesIntoSections, type ComposerGallerySectionKey } from './gallerySections';
import { resolveGalleryAddAction } from './resolveGalleryAddAction';
import './ComposerGalleryOrganism.scss';

export type ComposerGalleryKindFilter = 'all' | 'space' | CompositionKind | ComponentRole;

export interface ComposerGalleryEntry {
  id: string;
  targetKind: 'space' | 'composition' | 'catalog';
  kind: 'space' | CompositionKind | ComponentRole;
  name: string;
  description?: string;
  canEdit: boolean;
  /**
   * Code-backed `CATALOG` entry (EAINT-844 Phase F2) — immutable, shipped-with-the-platform
   * vocabulary. Renders a "Built-in" chip instead of the view-only chip and is excluded from the
   * "New from template" picker (it cannot be cloned).
   */
  builtIn?: boolean;
  /**
   * The card's live mini-render source (SC1). `undefined` = not yet fetched (falls back to the
   * kind glyph); `null` = fetched but the target has no renderable content yet (also the glyph).
   */
  previewDocument?: SpaceDocument | null;
}

export interface ComposerGalleryOrganismProps {
  entries: ComposerGalleryEntry[];
  loading?: boolean;
  errorMessage?: string;
  kindFilter: ComposerGalleryKindFilter;
  onKindFilterChange: (kind: ComposerGalleryKindFilter) => void;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  onOpen: (entry: ComposerGalleryEntry) => void;
  onNewBlankSpace: () => void;
  onNewBlankComposition: (kind: CompositionKind) => void;
  /** Opens the from-template picker (SC1) — the caller owns picking a source entry and creating from it. */
  onNewFromTemplate: () => void;
  /** "New with agent" deep link (SC8) — opens a blank Space with the agent thread focused. Absent hides the affordance (e.g. agent service not configured). */
  onNewWithAgent?: () => void;
  /** Resolves composition refs inside a previewed Space document. Absent = previews of Spaces referencing other compositions fall back to the unknown-component placeholder for those refs. */
  apiClient?: ApiClient;
}

const KIND_FILTERS: ComposerGalleryKindFilter[] = ['all', 'space', 'surface', 'section', 'fragment', 'primitive'];

/**
 * The `/compose` gallery tab (SC1): a search + kind-facet toolbar, New (blank Space / blank
 * composition-by-kind) affordances, and a permission-filtered card grid. Filtering is a pure
 * pass-through of `entries` — the caller (`ComposeGalleryPage`) is the one responsible for only
 * ever populating `entries` from `view`-permitted API rows; this organism adds no visibility of
 * its own on top of what it's handed.
 */
const ComposerGalleryOrganism: React.FC<ComposerGalleryOrganismProps> = ({
  entries,
  loading = false,
  errorMessage,
  kindFilter,
  onKindFilterChange,
  searchTerm,
  onSearchTermChange,
  onOpen,
  onNewBlankSpace,
  onNewBlankComposition,
  onNewFromTemplate,
  onNewWithAgent,
  apiClient,
}) => {
  const { t } = useTranslation();

  const addAction = resolveGalleryAddAction(kindFilter);

  // The `'all'` facet is the gallery's default state, so this menu — not the per-facet buttons —
  // is the only entry point most users ever see. It therefore has to carry every create route,
  // including "from template" (SC1) and "with agent" (SC8); dropping them here strands both
  // behind a facet click.
  const menuOptions = [
    { label: t('composer.gallery.new.space'), value: 'space' },
    { label: t('composer.gallery.new.surface'), value: 'surface' },
    { label: t('composer.gallery.new.section'), value: 'section' },
    { label: t('composer.gallery.new.fragment'), value: 'fragment' },
    { label: t('composer.gallery.new.fromTemplate'), value: 'fromTemplate' },
    ...(onNewWithAgent ? [{ label: t('composer.gallery.new.withAgent'), value: 'withAgent' }] : []),
  ];

  const handleMenuSelect = (value: string) => {
    if (value === 'space') return onNewBlankSpace();
    if (value === 'fromTemplate') return onNewFromTemplate();
    if (value === 'withAgent') return onNewWithAgent?.();
    return onNewBlankComposition(value as CompositionKind);
  };

  const sections = groupGalleryEntriesIntoSections(entries, kindFilter);

  const sectionHeader = (key: ComposerGallerySectionKey) => t(`composer.gallery.section.${key}`);

  return (
    <div className="composer-gallery" data-testid="composer-gallery">
      <div className="composer-gallery__toolbar">
        <InputAtom
          value={searchTerm}
          placeholder={t('composer.gallery.searchPlaceholder')}
          onChange={(e) => onSearchTermChange(e.target.value)}
          aria-label={t('composer.gallery.searchPlaceholder')}
        />
        <SegmentedControlAtom
          options={KIND_FILTERS.map((kind) => ({ value: kind, label: t(`composer.gallery.kind.${kind}`) }))}
          value={kindFilter}
          onChange={(value) => onKindFilterChange(value as ComposerGalleryKindFilter)}
          mode="single"
          variant="pills"
          size="small"
          ariaLabel={t('composer.gallery.kindFilterLabel')}
        />
        <div className="composer-gallery__new-actions">
          {addAction.type === 'space' && (
            <>
              <ButtonAtom variant="secondary" size="small" icon="pi pi-plus" onClick={onNewBlankSpace}>
                {t('composer.gallery.new.space')}
              </ButtonAtom>
              <ButtonAtom variant="secondary" size="small" icon="pi pi-copy" onClick={onNewFromTemplate}>
                {t('composer.gallery.new.fromTemplate')}
              </ButtonAtom>
              {onNewWithAgent && (
                <ButtonAtom variant="secondary" size="small" icon="pi pi-sparkles" onClick={onNewWithAgent}>
                  {t('composer.gallery.new.withAgent')}
                </ButtonAtom>
              )}
            </>
          )}
          {addAction.type === 'composition' && (
            <ButtonAtom
              variant="secondary"
              size="small"
              icon="pi pi-plus"
              onClick={() => onNewBlankComposition(addAction.kind)}
            >
              {t(`composer.gallery.new.newComposition.${addAction.kind}`)}
            </ButtonAtom>
          )}
          {addAction.type === 'menu' && (
            <DropdownAtom
              value={null}
              options={menuOptions}
              optionLabel="label"
              optionValue="value"
              placeholder={t('composer.gallery.new.menu')}
              aria-label={t('composer.gallery.new.menu')}
              onChange={(e) => handleMenuSelect(e.value as string)}
            />
          )}
        </div>
      </div>

      {errorMessage && (
        <p className="composer-gallery__error" role="alert">
          {errorMessage}
        </p>
      )}

      {loading ? (
        <p className="composer-gallery__loading">{t('composer.gallery.loading')}</p>
      ) : sections.length === 0 ? (
        <p className="composer-gallery__empty">{t('composer.gallery.empty')}</p>
      ) : (
        <div className="composer-gallery__sections">
          {sections.map((section) => (
            <div key={section.key} className="composer-gallery__section" data-testid={`composer-gallery-section-${section.key}`}>
              <h3 className="composer-gallery__section-header">{sectionHeader(section.key)}</h3>
              <div className="composer-gallery__grid">
                {section.entries.map((entry) => (
                  // A real <button> can't legally contain the preview's interactive descendants
                  // (its own resolved CompositionHost tree, which may render buttons/inputs). The
                  // preview is made `inert` (GalleryCardPreviewMolecule), so this div is the only
                  // interactive target in the card — hence div[role="button"] + explicit keyboard
                  // handling (Sonar S1082) rather than the canonical <button>.
                  <div
                    key={`${entry.targetKind}:${entry.id}`}
                    role="button"
                    tabIndex={0}
                    className="composer-gallery__card"
                    onClick={() => onOpen(entry)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      onOpen(entry);
                    }}
                    data-testid={`composer-gallery-card-${entry.id}`}
                  >
                    <div className="composer-gallery__card-preview" data-testid={`composer-gallery-preview-${entry.id}`}>
                      {entry.previewDocument ? (
                        <GalleryCardPreviewMolecule
                          document={entry.previewDocument}
                          apiClient={apiClient}
                          surfaceId={`composer-gallery-card-${entry.id}`}
                        />
                      ) : (
                        <span
                          className={`composer-gallery__card-glyph pi ${entry.targetKind === 'space' ? 'pi-th-large' : 'pi-box'}`}
                          aria-hidden="true"
                        />
                      )}
                    </div>
                    <div className="composer-gallery__card-header">
                      <span className="composer-gallery__card-name">{entry.name}</span>
                    </div>
                    {entry.description && <p className="composer-gallery__card-description">{entry.description}</p>}
                    <div className="composer-gallery__card-chips">
                      <ChipAtom label={t(`composer.gallery.kind.${entry.kind}`)} className="composer-gallery__card-kind" />
                      {entry.builtIn ? (
                        <ChipAtom
                          label={t('composer.gallery.builtIn')}
                          locked
                          lockedReason={t('composer.gallery.builtInReason')}
                          className="composer-gallery__card-builtin"
                        />
                      ) : (
                        !entry.canEdit && (
                          <ChipAtom
                            label={t('composer.gallery.viewOnly')}
                            icon="pi pi-eye"
                            className="composer-gallery__card-readonly"
                          />
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ComposerGalleryOrganism;
